import { DestroyRef, inject, Component, input, signal, viewChildren } from '@angular/core';

import type { CardData } from '@shared/components/hover-expandable-cards/hover-expandable-cards';
import { BreakpointService } from '@shared/services';

import { VideoPlayerComponent } from '../video-player/video-player';

const DEFAULT_DURATION = 5000;

@Component({
  selector: 'app-auto-expandable-cards',
  imports: [VideoPlayerComponent],
  templateUrl: './auto-expandable-cards.html',
})
export class AutoExpandableCardsComponent {
  private destroyRef = inject(DestroyRef);
  private breakpoints = inject(BreakpointService);

  videoPlayers = viewChildren(VideoPlayerComponent);

  cards = input.required<CardData[]>();

  activeIndex = signal(0);

  private started = false;
  private timeoutId?: ReturnType<typeof setTimeout>;

  constructor() {
    this.destroyRef.onDestroy(() => this.stopAutoRotation());
  }

  /** Called by LazyViewportDirective once a card's video is in view and its source is attached. */
  onVideoEntered(cardIndex: number): void {
    if (!this.breakpoints.isDesktop()) {
      const videoPlayer = this.videoPlayers()[cardIndex];

      if (videoPlayer) {
        videoPlayer.play();
      }

      return;
    }

    // Start the rotation timer once, when the section first comes into view.
    if (!this.started) {
      this.started = true;

      this.scheduleNext();
    }

    // Play the active card only when its own source is ready (siblings load independently).
    if (cardIndex === this.activeIndex()) {
      const videoPlayer = this.videoPlayers()[cardIndex];

      if (videoPlayer) {
        videoPlayer.setTime(0);
        videoPlayer.play();
      }
    }
  }

  onCardClick(cardIndex: number): void {
    if (!this.breakpoints.isDesktop()) {
      return;
    }

    this.goToCard(cardIndex);
  }

  private goToCard(cardIndex: number): void {
    this.activeIndex.set(cardIndex);
    this.activateVideos();
    this.scheduleNext();
  }

  private activateVideos(): void {
    this.videoPlayers().forEach((videoPlayer, playerIndex) => {
      if (playerIndex === this.activeIndex()) {
        videoPlayer.setTime(0);
        videoPlayer.play();
      } else {
        videoPlayer.pause();
      }
    });
  }

  private scheduleNext(): void {
    this.stopAutoRotation();

    const duration = this.cards()[this.activeIndex()]?.duration ?? DEFAULT_DURATION;

    this.timeoutId = setTimeout(() => {
      this.goToCard((this.activeIndex() + 1) % this.cards().length);
    }, duration);
  }

  private stopAutoRotation(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
  }
}
